import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

// Persistent nudge for makers who set up a profile but never listed anything.
// Stays until they have at least one listing (the query invalidates on create),
// and offers a "what's stopping you?" path so we learn why activation stalls.
export function ListFirstProductBanner() {
  const { isAuthenticated, user } = useAuth();
  const [, setLocation] = useLocation();
  const isSeller = Boolean((user as any)?.sellerType);

  const { data: items } = useQuery<unknown[]>({
    queryKey: ["/api/seller/items"],
    enabled: isAuthenticated && isSeller,
    queryFn: async () => {
      const res = await fetch("/api/seller/items", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  if (!isAuthenticated || !isSeller) return null;
  // Undefined while loading → render nothing. Only show once we know it's empty.
  if (!Array.isArray(items) || items.length > 0) return null;

  return (
    <div className="mx-3 my-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
          <Store className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">You haven't listed anything yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            List your first product so buyers nearby can find and order from you. It only takes
            a minute, and you can add a photo or just type the details.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => setLocation("/seller?action=add")}
              data-testid="list-first-product-cta"
            >
              List a product
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("marketlink:open-feedback", {
                    detail: {
                      category: "other",
                      prefill: "What's stopping me from listing my products: ",
                    },
                  }),
                )
              }
              data-testid="list-first-product-blocker"
            >
              What's stopping you?
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
